import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import {
    buttonClassNames,
    makeStyles,
    Menu,
    MenuItem,
    MenuItemProps,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Overflow,
    OverflowItem,
    OverflowItemProps,
    tokens,
    Toolbar,
    ToolbarButton,
    ToolbarButtonProps,
    ToolbarDivider,
    ToolbarProps,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components'
import type { ObserveOptions } from '@fluentui/priority-overflow'
import { useTranslation } from 'react-i18next'
import { bundleIcon, MoreVerticalFilled, MoreVerticalRegular } from '@fluentui/react-icons'

export const useOverflowToolbarPanelStyles = makeStyles({
    selectedMenuItem: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
    },
})

const useOverflowMenuStyles = makeStyles({
    // On narrow viewports a nested submenu has no room next to the parent menu and Fluent
    // positions it on top. Dimming the parent while a submenu is open makes it clear which
    // of the two overlapping menus holds the choices. Visual only — the parent stays
    // interactive so hovering another item still switches submenus. Uses filter instead of
    // opacity: the popover's enter/exit animation animates opacity and would override it.
    dimmedWhileSubmenuOpen: {
        filter: 'opacity(0.6)',
    },
})

const MoreIcon = bundleIcon(MoreVerticalFilled, MoreVerticalRegular)

// ToolbarButton is always rendered at size 'medium': useToolbarButton_unstable hardcodes
// `size: 'medium'` and reads neither its own `size` prop (which ends up on the <button> element as a
// stray attribute) nor the size from the Toolbar context. These classes put the toolbar's size back,
// with the values Fluent's own Button uses for that size. `medium` is deliberately absent - it is
// what a ToolbarButton already renders, so leaving it alone keeps every existing toolbar untouched.
const useToolbarButtonSizeStyles = makeStyles({
    small: {
        minWidth: '64px',
        padding: `3px ${tokens.spacingHorizontalS}`,
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        lineHeight: tokens.lineHeightBase200,
    },
    // A button without a label is square, so it gets Fluent's icon-only metrics instead of the
    // label-sized minimum width.
    smallIconOnly: {
        padding: '1px',
        minWidth: '24px',
        maxWidth: '24px',
    },
    large: {
        minWidth: '96px',
        padding: `8px ${tokens.spacingHorizontalL}`,
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase400,

        // The only size at which Fluent grows the icon along with the button.
        [`& .${buttonClassNames.icon}`]: {
            fontSize: '24px',
            height: '24px',
            width: '24px',
        },
    },
    largeIconOnly: {
        padding: '7px',
        minWidth: '40px',
        maxWidth: '40px',
    },
})

/**
 * The props for one ToolbarButton: whatever the host supplies, with the class for the toolbar's size
 * merged in front of it so a host className still wins.
 */
function useSizedToolbarButtonProps(props: OverflowToolbarPanelProps, button?: HammockToolbarButtonData): ToolbarButtonProps {
    const styles = useToolbarButtonSizeStyles()

    const buttonProps = props.getToolbarButtonProps?.(button)
    const size = props.toolbarProps?.size ?? 'medium'
    // Only the host can give a button a label (through getToolbarButtonProps); without one the button
    // renders its icon alone.
    const iconOnly = !buttonProps?.children

    return {
        ...buttonProps,
        className: mergeClasses(
            size === 'small' && styles.small,
            size === 'small' && iconOnly && styles.smallIconOnly,
            size === 'large' && styles.large,
            size === 'large' && iconOnly && styles.largeIconOnly,
            buttonProps?.className,
        ),
    }
}

export type HammockToolbarProps = Pick<ToolbarProps, 'className' | 'size' | 'checkedValues' | 'onCheckedValueChange'>

export type HammockToolbarButtonData = Omit<ToolbarButtonProps, 'onClick'> & {
    value: string
    titleKey?: string
    route?: string
    onClick?: (value: string) => void
    filter?: (button: HammockToolbarButtonData) => boolean
    disabled?: boolean
    // Translation key explaining why the button is disabled; shown as the tooltip while
    // `disabled` is true. Requires the host to render the button as disabledFocusable
    // (a natively disabled element gets no hover events, so no tooltip).
    disabledReasonKey?: string
    render?: (button: HammockToolbarButtonData, key: string | number) => ReactElement
    divider?: boolean
    childButtons?: HammockToolbarButtonData[]
}

const buttonTooltipContent = (button: HammockToolbarButtonData, translate: (key: string) => string): string => {
    if (button.disabled && button.disabledReasonKey) {
        return translate(button.disabledReasonKey)
    }
    return (button.title as string) ?? translate(button.titleKey)
}

type OverflowToolbarPanelProps = Partial<ObserveOptions> & {
    containerClassName?: string
    buttons: HammockToolbarButtonData[]
    filter?: (buttons: HammockToolbarButtonData[]) => HammockToolbarButtonData[]
    overflowButtonLabelKey: string

    toolbarProps?: HammockToolbarProps
    getToolbarButtonProps?: (button?: HammockToolbarButtonData) => ToolbarButtonProps
    getMenuItemProps?: (button: HammockToolbarButtonData) => MenuItemProps
}

type OverflowToolbarItemProps = OverflowToolbarPanelProps & {
    index: number
    button: HammockToolbarButtonData
}

type OverflowMenuItemProps = OverflowToolbarPanelProps &
    Pick<OverflowItemProps, 'id'> & {
        button: HammockToolbarButtonData
        onSubmenuOpenChange?: (id: string, open: boolean) => void
    }

// Renders the overflow-menu counterpart of ONE toolbar item. Visibility is checked on the
// same id the toolbar's OverflowItem registered (the button's value), so the menu shows
// exactly the buttons that fell out of the toolbar — a button with childButtons becomes a
// nested submenu carrying its (filtered) children.
function OverflowMenuItem(props: OverflowMenuItemProps): ReactElement {
    const { t: translate } = useTranslation()
    const isVisible = useIsOverflowItemVisible(props.id)

    if (isVisible || props.button.divider) {
        return null
    }

    const childButtons = props.filter?.(props.button.childButtons) || props.button.childButtons
    if (childButtons?.length > 0) {
        return (
            <Menu onOpenChange={(_, data) => props.onSubmenuOpenChange?.(props.id, data.open)}>
                <MenuTrigger disableButtonEnhancement>
                    <MenuItem {...props.getMenuItemProps?.(props.button)} icon={props.button.icon}>
                        {props.button.title ?? translate(props.button.titleKey)}
                    </MenuItem>
                </MenuTrigger>

                <MenuPopover>
                    <MenuList>
                        {childButtons.map(button =>
                            button.render ? (
                                button.render(button, button.value)
                            ) : (
                                <MenuItem
                                    key={button.value}
                                    {...props.getMenuItemProps?.(button)}
                                    icon={button.icon}
                                    onClick={() => button.onClick?.(button.value)}
                                >
                                    {button.title ?? translate(button.titleKey)}
                                </MenuItem>
                            ),
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>
        )
    }

    return (
        <MenuItem {...props.getMenuItemProps?.(props.button)} icon={props.button.icon} onClick={() => props.button.onClick?.(props.button.value)}>
            {props.button.title ?? translate(props.button.titleKey)}
        </MenuItem>
    )
}

function OverflowMenu(props: OverflowToolbarPanelProps): ReactElement {
    const { t: translate } = useTranslation()
    const styles = useOverflowMenuStyles()
    const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>()
    const overflowButtonProps = useSizedToolbarButtonProps(props, { value: 'overflow' })
    // Ids of the child submenus that are currently open (a Set instead of a boolean: when
    // hovering from one submenu trigger to another, the open of the second can arrive
    // before the close of the first).
    const [openSubmenuIds, setOpenSubmenuIds] = useState<Set<string>>(new Set())

    // The exact same list the toolbar rendered its OverflowItems from — ids must line up.
    const buttons = useMemo(() => props.filter?.(props.buttons) || props.buttons || [], [props.buttons, props.filter])

    if (!isOverflowing) {
        return null
    }

    const onSubmenuOpenChange = (id: string, open: boolean) =>
        setOpenSubmenuIds(previous => {
            const next = new Set(previous)
            if (open) {
                next.add(id)
            } else {
                next.delete(id)
            }
            return next
        })

    return (
        <Menu
            onOpenChange={(_, data) => {
                if (!data.open) {
                    setOpenSubmenuIds(new Set())
                }
            }}
        >
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`+${translate(props.overflowButtonLabelKey, { count: overflowCount })}`}>
                    <ToolbarButton key={'-1'} ref={ref} {...overflowButtonProps} icon={<MoreIcon />} />
                </Tooltip>
            </MenuTrigger>

            <MenuPopover className={openSubmenuIds.size > 0 ? styles.dimmedWhileSubmenuOpen : undefined}>
                <MenuList>
                    {buttons.map(button => (
                        <OverflowMenuItem key={button.value} id={button.value} button={button} onSubmenuOpenChange={onSubmenuOpenChange} {...props} />
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

//OverflowItem measures its child through a ref, so this component must forward it to the visible ToolbarButton
const OverflowToolbarItemMenu = React.forwardRef<HTMLButtonElement, OverflowToolbarItemProps>(function OverflowToolbarItemMenu(props, ref) {
    const { t: translate } = useTranslation()

    const childButtons = useMemo(() => props.filter?.(props.button.childButtons) || props.button.childButtons || [], [props.button])
    const buttonProps = useSizedToolbarButtonProps(props, props.button)

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={buttonTooltipContent(props.button, translate)}>
                    <ToolbarButton ref={ref} key={props.index} {...buttonProps} icon={props.button.icon} />
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {childButtons.map((button, index) =>
                        button.render ? (
                            button.render(button, `${props.index}-${index}`)
                        ) : (
                            <MenuItem
                                key={`${props.index}-${index}`}
                                {...props.getMenuItemProps?.(button)}
                                icon={button.icon}
                                onClick={() => button.onClick(button.value)}
                            >
                                {button.title ?? translate(button.titleKey)}
                            </MenuItem>
                        ),
                    )}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
})

function OverflowToolbarItem(props: OverflowToolbarItemProps): ReactElement {
    const { t: translate } = useTranslation()
    const buttonProps = useSizedToolbarButtonProps(props, props.button)

    const childButtons = useMemo(() => props.filter?.(props.button.childButtons) || props.button.childButtons || [], [props.button])

    return (
        // The id must be the button's value (stable and unique), NOT its position: the
        // overflow menu checks visibility per id against a filtered list whose positions
        // can differ from the toolbar's render order.
        <OverflowItem key={props.button.value} id={props.button.value}>
            {props.button.divider ? (
                <ToolbarDivider />
            ) : props.button.render ? (
                props.button.render(props.button, props.index)
            ) : childButtons.length > 0 ? (
                //No Tooltip wrapper here: OverflowToolbarItemMenu renders its own Tooltip around the trigger button
                <OverflowToolbarItemMenu {...props} />
            ) : (
                <Tooltip key={props.index} relationship={'label'} content={buttonTooltipContent(props.button, translate)}>
                    <ToolbarButton
                        key={props.index}
                        {...buttonProps}
                        icon={props.button.icon}
                        onClick={() => props.button.onClick?.(props.button.value)}
                    />
                </Tooltip>
            )}
        </OverflowItem>
    )
}

/**
 * Renders an Overflow for a Toolbar. All ToolbarButtons are wrapped so they can function as overflow button.
 * When a ToolbarButton contains children, it functions as Menu button.
 *
 * @param props
 * @constructor
 */
export default function OverflowToolbarPanel(props: OverflowToolbarPanelProps): ReactElement {
    return (
        <Overflow minimumVisible={props.minimumVisible ?? 3}>
            <div className={props.containerClassName}>
                <Toolbar {...props.toolbarProps}>
                    <>
                        {(props.filter?.(props.buttons) || props.buttons)?.map((button, index) => (
                            <OverflowToolbarItem key={button.value} index={index} button={button} {...props} />
                        ))}
                        <OverflowMenu {...props} />
                    </>
                </Toolbar>
            </div>
        </Overflow>
    )
}
